import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { uploadsService } from '@/shared/api/services/uploads.service';
import { cacheKeys } from '@/shared/lib/cache-invalidation';
import { projectAngleKeys } from '@/shared/api/hooks/use-project-angles';
import { wsLogger } from '@/shared/lib/logger';
import {
  VALIDATION_POLL_INTERVAL_MS,
  VALIDATION_WAIT_TIMEOUT_MS,
} from '@/features/angle/lib/validation-polling';

/**
 * L1 for a building: wait out the intakes that are still running when the page
 * opens, then reconcile what they made.
 *
 * The upload window watches its own batch, but it dies with the window — and
 * with the tab. Everything an intake produced (a view, a reference, the
 * server-computed rescue flag) lands on the building's own rows, so one refresh
 * at the end is the whole recovery; no per-file UI is rebuilt.
 *
 * Same cadence and the same single wait budget as the window's poll, so a
 * building whose queue never picks the work up stops being asked about.
 */
export function useUnfinishedIntakes(projectId: string | undefined): void {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!projectId) return;

    let cancelled = false;

    const run = async () => {
      let waitedMs = 0;
      let sawAny = false;

      while (!cancelled && waitedMs < VALIDATION_WAIT_TIMEOUT_MS) {
        let pending;
        try {
          pending = await uploadsService.listPending(projectId);
        } catch (error) {
          // A failed ask changes nothing — the lists are still whatever the
          // normal load left them.
          wsLogger.warn('[INTAKE] pending list failed:', error);
          return;
        }
        if (cancelled) return;
        if (pending.length === 0) break;

        sawAny = true;
        await new Promise((resolve) =>
          setTimeout(resolve, VALIDATION_POLL_INTERVAL_MS)
        );
        waitedMs += VALIDATION_POLL_INTERVAL_MS;
      }

      if (cancelled || !sawAny) return;

      wsLogger.info(`[INTAKE] unfinished intakes of ${projectId} settled`);
      queryClient.invalidateQueries({
        queryKey: projectAngleKeys.list(projectId),
      });
      queryClient.invalidateQueries({
        queryKey: cacheKeys.references.listsByProject(projectId),
      });
      queryClient.invalidateQueries({
        queryKey: cacheKeys.projects.detail(projectId),
      });
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [projectId, queryClient]);
}
